/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { STARCTypography } from '@/components/STARCTypography';
import {
  CMSSpacer,
  type CMSSpacerProps,
} from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import { type ContentStackStorePageBody } from '@/features/contentstack/interface';
import { CMSIconTitleTextInfo } from '@/features/contentstack/components/CMSIconTitleTextInfo/CMSIconTitleTextInfo';
import { CMSFeaturedSpotlight } from '@/features/contentstack/components/CMSFeaturedSpotlight/CMSFeaturedSpotlight';
import { CMSProductTypeGrid } from '@/features/contentstack/components/CMSProductTypeGrid/CMSProductTypeGrid';
import { CMSGrid } from '@/features/contentstack/components/CMSGrid/CMSGrid';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSFaq } from '@/features/contentstack/components/CMSFaq/CMSFaq';
import { LocationsBreadcrumbs } from '../LocationsBreadcrumbs/LocationsBreadcrumbs';
import { StoreInformation } from '../StoreInformation/StoreInformation';
import { StoreDescription } from '../StoreDescription/StoreDescription';
import { CMSVideoContent } from '@/features/contentstack/components/CMSVideoContent/CMSVideoContent';
import { NearestStores } from '../NearestStores/NearestStores';
import type { ContentStackStorePageType } from '@/features/contentstack/interface/models';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';

const CMSStoreSpacer = ({ children, ...rest }: { id?: string } & CMSSpacerProps) => (
  <CMSSpacer landingPage {...rest}>
    {children}
  </CMSSpacer>
);

type CMSModularHandlerProps = {
  content: Array<Partial<ContentStackStorePageBody>> | undefined;
  pageType: ContentStackStorePageType;
  seoCopyBlock: string | undefined;
};

export const CMSLocationsModularHandler = ({
  content,
  pageType,
  seoCopyBlock,
}: CMSModularHandlerProps) => {
  if (!content) {
    return null;
  }

  return (
    <STARCTypography>
      {content.map((modularBlock, i) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        if (modularBlock.breadcrumbs?.enabled) {
          return (
            <CMSStoreSpacer key={`breadcrumbs-${i}`} id={getBlockId('breadcrumbs')}>
              <LocationsBreadcrumbs noHorizontalMargin noVerticalMargin pageType={pageType} />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.store_information?.enabled) {
          return (
            <CMSStoreSpacer key={`store-information-${i}`} id={getBlockId('store_information')}>
              <StoreInformation warningText={modularBlock.store_information.warning_notification} />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.icon_title_text_info?.icon_title_text_info?.[0]) {
          return (
            <CMSStoreSpacer
              key={`icon-title-text-info-${i}`}
              id={getBlockId('icon_title_text_info')}
            >
              <CMSIconTitleTextInfo
                content={modularBlock.icon_title_text_info.icon_title_text_info[0]}
              />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.featured_spotlight?.featured_spotlight?.[0]) {
          return (
            <CMSStoreSpacer key={`featured-spotlight-${i}`} id={getBlockId('featured_spotlight')}>
              <CMSFeaturedSpotlight
                content={modularBlock.featured_spotlight.featured_spotlight[0]}
              />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.product_grid?.product_grid?.[0]) {
          return (
            <CMSStoreSpacer key={`product-grid-${i}`} id={getBlockId('product_grid')}>
              <CMSProductTypeGrid
                content={modularBlock.product_grid.product_grid[0]}
                gridIndex={i}
              />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.parttype_grid?.parttype_grid?.[0]) {
          return (
            <CMSStoreSpacer key={`part-type-grid-${i}`} id={getBlockId('parttype_grid')}>
              <CMSGrid content={modularBlock.parttype_grid.parttype_grid[0]} />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.banner?.banner?.[0]) {
          return (
            <CMSStoreSpacer key={`banner-${i}`} id={getBlockId('banner')}>
              <CMSBanner content={modularBlock.banner.banner[0]} />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.nearby_stores?.enabled) {
          return (
            <CMSStoreSpacer key={`nearby-stores-${i}`} id={getBlockId('nearby_stores')}>
              <div>
                <NearestStores title={modularBlock.nearby_stores.heading} />
              </div>
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.video_content?.video_content?.[0]) {
          return (
            <CMSStoreSpacer key={`image-video-${i}`} id={getBlockId('video_content')}>
              <CMSVideoContent
                content={modularBlock.video_content.video_content[0]}
                preload="none"
              />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.store_description?.enabled) {
          return (
            <CMSStoreSpacer key={`store-description-${i}`} id={getBlockId('store_description')}>
              <StoreDescription
                content={modularBlock.store_description}
                seoCopyBlock={seoCopyBlock}
              />
            </CMSStoreSpacer>
          );
        }

        if (modularBlock.faq?.faq?.[0]) {
          return (
            <CMSStoreSpacer key={`faqs-${i}`} id={getBlockId('faq')}>
              <CMSFaq content={modularBlock.faq.faq[0]} />
            </CMSStoreSpacer>
          );
        }

        return null;
      })}
    </STARCTypography>
  );
};
